import {
  COUNTRY_SELECT_OPTIONS,
  NATIONALITY_NAME_OPTIONS,
  NATIONALITY_OPTIONS,
} from '@ben/common/countries';
import { getSubdivisionLabelForCode } from '@ben/common/subdivisions';
import type { ComboboxItem } from '@mantine/core';
import type { WidgetProps } from '@rjsf/utils';
import { isNil } from 'lodash';
import { useCallback, useMemo } from 'react';
import DeprecatedSelect from '../../deprecated-select';
import Select from '../../select';
import {
  mapEnumOptionsToComboboxItems,
  prependComboboxItemWhenValueMissing,
} from './utils/combobox-select-data';

const SelectWidget = ({
  id,
  label,
  value,
  disabled,
  onChange,
  onBlur,
  onFocus,
  rawErrors,
  hideError,
  options,
  placeholder,
  schema,
  uiSchema,
  registry,
  onCreateOption,
}: WidgetProps) => {
  const formContext = registry?.formContext as
    | { clearableFields?: boolean }
    | undefined;
  const isCountrySelect = schema.format === 'country';
  const isNationalitySelect =
    schema.format === 'nationality' || schema.format === 'nationality-names';
  const isSubdivisionSelect = schema.format === 'subdivision';
  const onSelectChange = useCallback(
    (newValue: string | string[] | null) => onChange(newValue || undefined),
    [onChange]
  );
  const onSingleSelectChange = useCallback(
    (newValue?: string) => onChange(newValue || undefined),
    [onChange]
  );
  const onSelectBlur = useCallback(
    () => onBlur(id, value),
    [id, onBlur, value]
  );
  const onSelectFocus = useCallback(
    () => onFocus(id, value),
    [id, onFocus, value]
  );
  const hasError = rawErrors && rawErrors.length > 0 && !hideError;

  // Drive the rendering off the schema rather than the runtime type of the
  // value, so an empty or cleared array field cannot flip to a single select
  // and start storing a string where the schema requires an array
  const isMultiSelect = schema.type === 'array' || Array.isArray(value);

  const multiSelectValue = useMemo(
    function toMultiSelectValue(): string[] {
      if (Array.isArray(value)) {
        return value as string[];
      }
      return isNil(value) || value === '' ? [] : [String(value)];
    },
    [value]
  );

  const data = useMemo((): ComboboxItem[] => {
    if (isCountrySelect) {
      return COUNTRY_SELECT_OPTIONS;
    }

    if (isNationalitySelect) {
      const nationalityOptions =
        schema.format === 'nationality-names'
          ? NATIONALITY_NAME_OPTIONS
          : NATIONALITY_OPTIONS;

      return prependComboboxItemWhenValueMissing(
        nationalityOptions,
        value,
        (valueString) => valueString
      );
    }

    const enumOptions = options?.enumOptions ?? [];

    if (isSubdivisionSelect) {
      const subdivisionItems = mapEnumOptionsToComboboxItems(
        enumOptions,
        getSubdivisionLabelForCode
      );
      return prependComboboxItemWhenValueMissing(
        subdivisionItems,
        value,
        getSubdivisionLabelForCode
      );
    }

    return enumOptions;
  }, [
    isCountrySelect,
    isNationalitySelect,
    isSubdivisionSelect,
    options,
    schema,
    value,
  ]);

  if (!isMultiSelect) {
    return (
      <Select
        id={id}
        label={label}
        value={value}
        data={data}
        disabled={disabled || schema.readOnly}
        errorMessage={rawErrors?.[0]}
        placeholder={placeholder}
        onChange={onSingleSelectChange}
        onBlur={onSelectBlur}
        onFocus={onSelectFocus}
        description={schema?.description}
        clearable={uiSchema?.clearable || formContext?.clearableFields}
        searchable={
          !!schema.properties?.['search'] ||
          isCountrySelect ||
          isNationalitySelect ||
          isSubdivisionSelect
        }
        comboboxProps={{ withinPortal: false }} // In case forms are rendered in drawers or modals
      />
    );
  }

  // Use deprecated multi-select for array schemas
  return (
    <DeprecatedSelect
      id={id}
      name={label}
      value={multiSelectValue}
      disabled={disabled || schema.readOnly}
      hasError={hasError}
      errorMessage={rawErrors?.[0]}
      options={data.map((option) => ({
        key: option.value,
        label: option.label,
      }))}
      placeholder={placeholder || ''}
      onChange={onSelectChange}
      onBlur={onSelectBlur}
      onFocus={onSelectFocus}
      width="100%"
      hintText={schema?.description}
      clearable={uiSchema?.clearable || formContext?.clearableFields}
      showSearch={!!schema.properties?.['search']}
      canCreateOption={!!onCreateOption}
      onCreateOption={onCreateOption}
    />
  );
};

export default SelectWidget;
